import type React from 'react';
import { memo } from 'react';
import styled from 'styled-components';

import styles from '@front/common/src/components/icon-button/styles';
import type { IconButtonCommonProps, StyledIconButtonProps } from '@front/common/src/components/icon-button/types';
import colors from '@front/common/src/utils/colors';

import { translate } from '@front/i18n';

import { getGradient } from '../../styles/gradient';
import { boxShadow } from '../../styles/mixins';
import Icon from '../icon/icon';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import type { WithClassNameProps } from '../types';
import { StylelessButton } from '../utils';
import { StyledLoadingWrapper } from './common';

const t = translate('components.button');

const disabledStyle = `
  ${styles.disabled}
  cursor: not-allowed;
`;

export const Button = styled(StylelessButton)<StyledIconButtonProps>`
  background: ${({ $backgroundColor }) => $backgroundColor};

  &:hover {
    ${({ $isDisabled, $isGradientBackgroundColor }) =>
      !$isDisabled &&
      `
        background: ${$isGradientBackgroundColor ? getGradient('gradient110') : colors.ink10};
      `}
  }

  &:active {
    ${({ $isDisabled, $isGradientBackgroundColor }) =>
      !$isDisabled &&
      `
        background: ${$isGradientBackgroundColor ? getGradient('gradient120') : colors.ink20};
      `}
  }
  ${styles.button}
  ${boxShadow.normal}
  ${({ $isDisabled }) => $isDisabled && disabledStyle}
`;

export const StyledIcon = styled(Icon)`
  position: absolute;
`;

export type IconButtonProps = IconButtonCommonProps &
  WithClassNameProps & {
    onPress: React.MouseEventHandler<HTMLButtonElement>;
    isLoading?: boolean;
  };

const IconButton = ({
  className,
  iconName,
  iconSize = 10,
  isDisabled = false,
  isLoading = false,
  label,
  onPress,
  size = 16,
  type = 'primary',
}: IconButtonProps) => {
  const { backgroundColor, borderColor, iconColor } = styles.colors[type];
  const isGradientBackgroundColor = backgroundColor === 'gradient';
  const shouldBeDisabled = !!(isDisabled || isLoading);

  return (
    <Button
      $backgroundColor={isGradientBackgroundColor ? getGradient() : backgroundColor}
      $isGradientBackgroundColor={isGradientBackgroundColor}
      $shouldHideBorderWidth={isGradientBackgroundColor}
      $borderColor={borderColor}
      $isDisabled={shouldBeDisabled}
      $size={size}
      aria-label={label}
      className={className}
      disabled={shouldBeDisabled}
      onClick={onPress}
      title={label}
      type='button'
    >
      {isLoading ? (
        <StyledLoadingWrapper>
          <LoadingSpinner color={iconColor} title={t('.loading')} />
        </StyledLoadingWrapper>
      ) : (
        <StyledIcon name={iconName} color={iconColor} size={iconSize} />
      )}
    </Button>
  );
};

export default memo(IconButton);
